import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { User } from '@paragon/contract-library';
import { logIn } from '@paragon/login-tools';

import { UpdatedUserData } from '../actions/userActions';

interface UserDataState {
  readonly users: { readonly [email: string]: User };
  readonly userToEdit: string | null;
}

const initialState: UserDataState = {
  users: {},
  userToEdit: null,
};

const userDataSlice = createSlice({
  name: 'userData',
  initialState,
  reducers: {
    usersSet: (state, action: PayloadAction<readonly User[]>) => {
      for (const user of action.payload) {
        state.users[user.email] = castDraft(user);
      }
    },
    usersAdd: (state, action: PayloadAction<User>) => {
      state.users[action.payload.email] = castDraft(action.payload);
    },
    selectUser: (state, action: PayloadAction<string>) => {
      state.userToEdit = action.payload;
    },
    usersMerge: (
      state,
      action: PayloadAction<{
        readonly emailOfUserToUpdate: string | null;
        readonly updatedData: UpdatedUserData;
      }>,
    ) => {
      const { updatedData } = action.payload;
      const emailOfUserToUpdate = action.payload.emailOfUserToUpdate ?? state.userToEdit!;

      state.users[emailOfUserToUpdate] = castDraft({ ...state.users[emailOfUserToUpdate], ...updatedData });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logIn, (state, action) => {
      const loggedInUser = action.payload.user;
      if (!state.users[loggedInUser.email]) {
        state.users[loggedInUser.email] = castDraft(loggedInUser);
      }
      state.userToEdit = loggedInUser.email;
    });
  },
});

export const { usersSet, usersAdd, selectUser, usersMerge } = userDataSlice.actions;
export const userData = userDataSlice.reducer;
